import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { Container, Button } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import AgentFlow from '../components/AgentFlow';
import { AnalysisAPI } from '../services/api';
import { ProjectApi } from '../services/projectApi';
import { useToastNotifications } from '../context/ToastContext';
import { AgentFlowStep, TenderAnalysisState } from '../types';

const AnalysisContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0;
`;

const AnalysisHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const AnalysisTitle = styled.h1`
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily.display};
`;

const AnalysisSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: ${theme.spacing[6]};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    align-items: stretch;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: ${theme.spacing[4]};
`;

const BreadcrumbLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary[600]};
  cursor: pointer;
  text-decoration: underline;
  font-size: ${theme.typography.fontSize.sm};
  
  &:hover {
    color: ${theme.colors.primary[700]};
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}20;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  color: ${theme.colors.error[700]};
  text-align: center;
  font-weight: ${theme.typography.fontWeight.medium};
`;

const LoadingMessage = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[6]};
`;

const ProjectAnalysisDetailsPage: React.FC = () => {
  const { projectId, threadId } = useParams<{ projectId: string; threadId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [analysisData, setAnalysisData] = useState<TenderAnalysisState | null>(null);
  const { showError } = useToastNotifications();

  // Default steps for completed analysis
  const defaultSteps: AgentFlowStep[] = [
    {
      id: 'process_pdf',
      name: 'Document Processing',
      status: 'completed',
      description: 'Extracted and processed the tender document content using OCR and text analysis.',
    },
    {
      id: 'initial_assessment',
      name: 'Initial Assessment',
      status: 'completed',
      description: 'AI reviewer analyzed responses and generated initial scoring report.',
    },
    {
      id: 'response_refinement',
      name: 'Response Refinement',
      status: 'completed',
      description: 'Optimized responses based on successful tender patterns and writing style analysis.',
    },
    {
      id: 'performance_analysis',
      name: 'Performance Analysis',
      status: 'completed',
      description: 'Analyzed potential ROI and competitive positioning of refined responses.',
    },
    {
      id: 'final_report',
      name: 'Final Report',
      status: 'completed',
      description: 'Generated comprehensive analysis report and deliverable documents.',
    },
  ];

  const fetchAnalysisData = async () => {
    if (!threadId || !projectId) {
      setError('Missing project ID or thread ID');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch project name
      const projectResponse = await ProjectApi.getProject(projectId);
      if (projectResponse.success && projectResponse.data) {
        setProjectName(projectResponse.data.name);
      }

      // Fetch analysis session data
      const analysisResponse = await AnalysisAPI.getAnalysisSession(threadId);
      
      if (!analysisResponse.success || !analysisResponse.data) {
        throw new Error(analysisResponse.error || 'Failed to fetch analysis data');
      }

      const sessionData = analysisResponse.data;
      const results = sessionData.results;

      if (!results) {
        throw new Error('No analysis results found');
      }

      // Transform the results to match the expected format
      const transformedData: TenderAnalysisState = {
        url: sessionData.url || '',
        successful_tender_urls: sessionData.successful_tender_urls || [],
        company_id: sessionData.company_id || '',
        questions: results.questions || [],
        evaluation_criteria: results.evaluation_criteria || [],
        requirements_summary: results.requirements_summary || '',
        initial_review_result: results.initial_review_result || null,
        refined_questions: results.refined_questions || [],
        improvement_analysis: results.improvement_analysis || null,
        comparison_path: results.comparison_path || '',
        final_report: results.final_report || null,
      };

      setAnalysisData(transformedData);

    } catch (error) {
      console.error('Error fetching analysis data:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
      showError('Error Loading Analysis', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysisData();
  }, [threadId, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackToProject = () => {
    navigate(`/projects/${projectId}`);
  };

  const handleBackToProjects = () => {
    navigate('/projects');
  };

  // Progress object for completed analysis
  const completedProgress = {
    currentStep: 'completed',
    progress: 100,
    message: 'Analysis completed successfully',
    isComplete: true,
    hasError: false,
  };

  return (
    <AnalysisContainer>
      <Container>
        <Breadcrumb>
          <BreadcrumbLink onClick={handleBackToProjects}>Projects</BreadcrumbLink>
          <span>›</span>
          <BreadcrumbLink onClick={handleBackToProject}>{projectName || 'Project'}</BreadcrumbLink>
          <span>›</span>
          <span>Analysis Details</span>
        </Breadcrumb>

        <AnalysisHeader>
          <AnalysisTitle>Analysis Details</AnalysisTitle>
          <AnalysisSubtitle>
            Comprehensive analysis results for {projectName}
          </AnalysisSubtitle>
        </AnalysisHeader>

        <ActionBar>
          <Button $variant="ghost" onClick={handleBackToProject}>
            <ArrowLeft size={20} />
            Back to Project
          </Button>
        </ActionBar>

        {isLoading && (
          <LoadingMessage>
            <FileText size={48} style={{ marginBottom: theme.spacing[4] }} />
            <p>Loading analysis details...</p>
          </LoadingMessage>
        )}

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} style={{ marginRight: theme.spacing[2] }} />
            {error}
          </ErrorMessage>
        )}

        {!isLoading && !error && analysisData && (
          <AgentFlow 
            steps={defaultSteps}
            isLoading={false}
            threadId={threadId}
            isCompletedAnalysis={true}
            progress={completedProgress}
            analysisData={analysisData}
          />
        )}

        {!isLoading && !error && !analysisData && (
          <ErrorMessage>
            <AlertCircle size={20} style={{ marginRight: theme.spacing[2] }} />
            No analysis data available
          </ErrorMessage>
        )}
      </Container>
    </AnalysisContainer>
  );
};

export default ProjectAnalysisDetailsPage;